import type { FetchLike } from '../screens/documents/viewer-core.js';

/**
 * Ek önizlemesinin çekirdeği (ortak 2026-10-24 madde 4; ADR 0126): hangi ekin "göz" simgesi
 * olacağı, dosyanın okunması ve türünün doğrulanması. Çizim `AttachmentPreview.tsx`'tedir;
 * buradakiler Node'da sınanır.
 *
 * Güvenlik:
 * - Dosya indirme ucundan okunur (gemide `felix-file://attachment/<id>`, ofiste
 *   `/api/office/attachments/<id>`): yetki indirmeyle aynıdır.
 * - Tür dosya adından ya da listedeki ipucundan değil, sunucunun `Content-Type` başlığından
 *   alınır; sunucu türü yüklemede içerikten belirlemiştir (K15). İçeriğin ilk baytları türüyle
 *   ayrıca karşılaştırılır.
 * - SVG hiç önizlenmez (betik taşıyabilir); yalnız indirilir.
 * - Dosya bellekte `Blob` olur; resim `objectURL` ile gösterilir, pencere kapanınca bırakılır.
 */

export type PreviewKind = 'image' | 'pdf';

/** Önizlenen türler; başka her şey (SVG dahil) yalnız indirilir. */
const PREVIEW_TYPES: Readonly<Record<string, PreviewKind>> = {
  'image/jpeg': 'image',
  'image/png': 'image',
  'image/webp': 'image',
  'image/gif': 'image',
  'application/pdf': 'pdf',
};

/** Ek en çok 25 MB'tır (K15); önizleme belleğe aldığı için üst sınır payıyla 64 MB. */
export const MAX_ATTACHMENT_PREVIEW_BYTES = 64 * 1024 * 1024;

function bareType(type: string | null | undefined): string {
  return (type ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
}

/** Sunucunun bildirdiği türün önizleme biçimi; SVG ve bilinmeyen tür `null`. */
export function previewKindOf(contentType: string | null | undefined): PreviewKind | null {
  const type = bareType(contentType);
  return Object.hasOwn(PREVIEW_TYPES, type) ? (PREVIEW_TYPES[type] ?? null) : null;
}

/** Listedeki tür ipucuna göre "göz" simgesi gösterilir mi (açılışta sunucu yeniden doğrular). */
export function canPreview(mimeType: string | null | undefined): boolean {
  return previewKindOf(mimeType) !== null;
}

const ascii = (text: string) => Array.from(text, (c) => c.charCodeAt(0));

/** İçeriğin ilk baytları: `null` herhangi bir bayt. */
const SIGNATURES: Readonly<Record<string, readonly (readonly (number | null)[])[]>> = {
  'image/jpeg': [[0xff, 0xd8, 0xff]],
  'image/png': [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  'image/gif': [ascii('GIF87a'), ascii('GIF89a')],
  'image/webp': [[...ascii('RIFF'), null, null, null, null, ...ascii('WEBP')]],
  'application/pdf': [ascii('%PDF-')],
};

function matchesSignature(type: string, head: Uint8Array): boolean {
  return (SIGNATURES[type] ?? []).some(
    (signature) =>
      head.length >= signature.length &&
      signature.every((byte, i) => byte === null || head[i] === byte),
  );
}

/** Hata: `code` arayüzde `attachmentPreview.errors.<code>` ile çevrilir. */
export class PreviewError extends Error {
  readonly code: string;
  constructor(code: string) {
    super(code);
    this.name = 'PreviewError';
    this.code = code;
  }
}

async function failure(response: Response): Promise<PreviewError> {
  let code = `HTTP_${response.status}`;
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body?.error === 'string') code = body.error;
  } catch {
    // Gövde JSON değil: HTTP kodu kalır
  }
  return new PreviewError(code);
}

/** Gövde en çok `limit` bayt okunur; aşan dosya okunmaz (bildirilen boy güvenilmez). */
async function readBounded(response: Response, limit: number): Promise<Uint8Array> {
  const declared = Number(response.headers.get('content-length') ?? Number.NaN);
  if (Number.isFinite(declared) && declared > limit) {
    await response.body?.cancel().catch(() => undefined);
    throw new PreviewError('PREVIEW_TOO_LARGE');
  }
  if (!response.body) return new Uint8Array(await response.arrayBuffer());
  const reader = response.body.getReader();
  const parts: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.byteLength;
    if (total > limit) {
      await reader.cancel().catch(() => undefined);
      throw new PreviewError('PREVIEW_TOO_LARGE');
    }
    parts.push(value);
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.byteLength;
  }
  return out;
}

/**
 * Dosyayı indirme ucundan okur ve türünü doğrular. Önizlenmeyen tür `PREVIEW_UNSUPPORTED`,
 * sunucu hatası kendi koduyla (`FORBIDDEN`, `FILE_NOT_UPLOADED`, `FILE_NOT_ON_BOARD`…) döner.
 */
export async function loadPreview(
  url: string,
  fetchImpl: FetchLike = (input, init) => fetch(input, init),
  signal?: AbortSignal,
): Promise<{ kind: PreviewKind; blob: Blob }> {
  const response = await fetchImpl(url, {
    credentials: 'same-origin',
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) throw await failure(response);
  const type = bareType(response.headers.get('content-type'));
  const kind = previewKindOf(type);
  if (!kind) {
    await response.body?.cancel().catch(() => undefined);
    throw new PreviewError('PREVIEW_UNSUPPORTED');
  }
  const bytes = await readBounded(response, MAX_ATTACHMENT_PREVIEW_BYTES);
  if (!matchesSignature(type, bytes.subarray(0, 16))) throw new PreviewError('PREVIEW_UNSUPPORTED');
  return { kind, blob: new Blob([bytes as Uint8Array<ArrayBuffer>], { type }) };
}

/**
 * PDF görüntüleyicinin (`openPdf`) parça okuyucusu bellekteki blobdan beslenir: dosya bir kez
 * indirilir, `Range` istekleri ağa gitmez.
 */
export function blobRangeFetch(blob: Blob): FetchLike {
  return async (_url, init) => {
    const range = /^bytes=(\d+)-(\d+)?$/.exec(init?.headers?.Range ?? '');
    if (!range) {
      return new Response(blob, {
        status: 200,
        headers: { 'content-length': String(blob.size) },
      });
    }
    const start = Number(range[1]);
    const end = Math.min(range[2] === undefined ? blob.size - 1 : Number(range[2]), blob.size - 1);
    if (start >= blob.size || end < start) {
      return new Response(null, {
        status: 416,
        headers: { 'content-range': `bytes */${blob.size}` },
      });
    }
    return new Response(blob.slice(start, end + 1), {
      status: 206,
      headers: {
        'content-range': `bytes ${start}-${end}/${blob.size}`,
        'content-length': String(end - start + 1),
      },
    });
  };
}
